import styled, { css } from 'styled-components'
import { NotificationTypeIcon } from '../NotificationTypeIcon'
import { overlayColors } from '@sketch/utils'

export const STATE_DOT_SIZE = 6
export const PREVIEW_SIZE = 40

export const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;

  /* Hidden by default, sliding in from the right as it fades. The hide is
   * immediate; the show waits 0.2s so a passing hover doesn't flash them. */
  opacity: 0;
  pointer-events: none;
  transform: translateX(8px);
  transition:
    opacity 150ms ease,
    transform 150ms ease;

  /* Fill the full row height and sit flush to the right padding. */
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;

  /* Frost the content the buttons overlap, fading the blur in from the left so
   * it blends into the row rather than cutting off abruptly. */
  padding: 0 12px 0 20px;
  border-radius: 8px;
  background: ${({ theme }) =>
    overlayColors(
      theme.colors.background.secondary.B,
      theme.colors.background.tertiary.D
    )};

  mask-image: linear-gradient(to right, transparent, #000 20px);
  -webkit-mask-image: linear-gradient(to right, transparent, #000 20px);
`

export const Root = styled.div<{ $isDarkMode: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px 14px 18px;
  border-radius: 10px;
  cursor: pointer;
  position: relative;
  overflow: hidden;

  color: ${({ theme }) => theme.colors.foreground.secondary.A};

  &:hover,
  &:focus-within {
    background: ${({ theme }) => theme.colors.background.tertiary.D};
  }

  &[aria-selected='true'] {
    background: ${({ theme, $isDarkMode }) =>
      $isDarkMode ? theme.colors.sketch.A : theme.colors.sketch.F};
    color: ${({ theme, $isDarkMode }) =>
      $isDarkMode
        ? theme.colors.foreground.secondary.A
        : theme.colors.sketch.A};
  }

  &[aria-selected='true'] ${NotificationTypeIcon} {
    background: ${({ theme, $isDarkMode }) =>
      $isDarkMode
        ? theme.colors.foreground.secondary.A
        : theme.colors.sketch.A};
    color: ${({ theme, $isDarkMode }) =>
      $isDarkMode ? theme.colors.sketch.A : theme.colors.foreground.primary.A};
  }

  &[aria-selected='true'] ${Actions} {
    background: ${({ theme, $isDarkMode }) =>
      $isDarkMode
        ? overlayColors(
            theme.colors.background.secondary.A,
            theme.colors.sketch.A
          )
        : overlayColors(
            theme.colors.background.secondary.A,
            theme.colors.sketch.F
          )};
  }

  &:hover ${Actions}, &:focus-within ${Actions} {
    opacity: 1;
    pointer-events: auto;
    transform: translateX(0);
    transition-delay: 0.2s;
  }
`

export const StateDot = styled.span<{ $visible: boolean }>`
  width: ${STATE_DOT_SIZE}px;
  height: ${STATE_DOT_SIZE}px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.sketch.A};
  flex-shrink: 0;
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  position: absolute;

  inset: auto auto auto 7px;
`

export const PreviewWrapper = styled.div`
  position: relative;
  width: ${PREVIEW_SIZE}px;
  height: ${PREVIEW_SIZE}px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const TypeIconOverlay = styled.span`
  position: absolute;
  line-height: 1;
  right: -4px;
  bottom: -4px;
`

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`

export const Title = styled.div`
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  font-size: 14px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const Subtitle = styled.div`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0.55;
`

export const Meta = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: 12px;
`

const swap = css`
  transition: opacity 120ms ease;
`

export const Timeago = styled.span`
  ${swap};

  opacity: 0.25;
  font-size: 12px;
  font-weight: ${({ theme }) => theme.fontWeights.regular};

  ${Root}:hover &,
  ${Root}:focus-within & {
    opacity: 0;
    pointer-events: none;
  }
`
